// Updated Web3Context.js - Always read from SKALE, keep network switching for writes

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import contractAbi from '../utils/abi.json';
import ApiService from '../services/api';

const Web3Context = createContext();

export function useWeb3() {
  return useContext(Web3Context);
}

// Mobile detection utility
const isMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

export function Web3Provider({ children }) {
  // Ethereum wallet state
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [contract, setContract] = useState(null);
  const [account, setAccount] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [networkName, setNetworkName] = useState('');
  const [passport, setPassport] = useState(null);
  const [passportLoading, setPassportLoading] = useState(false);
  const [supportedCategories, setSupportedCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  // Solana wallet state
  const [solanaWallet, setSolanaWallet] = useState(null);
  const [solanaConnected, setSolanaConnected] = useState(false);
  const [solanaPublicKey, setSolanaPublicKey] = useState(null);
  const [solanaWalletType, setSolanaWalletType] = useState(null);

  // Helper function to poll transaction status
  const pollTransactionStatus = async (transactionId, maxAttempts = 60) => {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/passport/transaction/${transactionId}`, {
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        
        const transaction = await response.json();
        
        if (transaction.status === 'COMPLETED') {
          return { 
            success: true, 
            result: transaction.result, 
            passport: transaction.passport 
          };
        }
        
        if (transaction.status === 'FAILED') {
          return { 
            success: false, 
            error: transaction.error,
            errorCategory: transaction.errorCategory,
            isPermanentFailure: transaction.isPermanentFailure,
            suggestions: transaction.suggestions,
            technicalError: transaction.technicalError
          };
        }
        
        await new Promise(resolve => setTimeout(resolve, 2000));
      } catch (error) {
        console.error(`Polling attempt ${attempt + 1} failed:`, error);
        if (attempt === maxAttempts - 1) {
          return { success: false, error: 'Transaction polling timeout' };
        }
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    }
    
    return { success: false, error: 'Transaction timeout' };
  };

  // Network-agnostic wallet connection (users can connect from any network)
  const connectWallet = useCallback(async () => {
    if (isConnecting) {
      console.log('Connection already in progress, skipping...');
      return;
    }
    
    setIsConnecting(true);
    setError(null);
    
    try {
      let newProvider;
      
      if (isMobile()) {
        if (window.ethereum) {
          console.log('Using injected provider on mobile');
          newProvider = new ethers.providers.Web3Provider(window.ethereum);
        } else {
          setError('Please install MetaMask or another Web3 wallet app, then open this website in the wallet\'s browser.');
          setIsConnecting(false);
          return;
        }
      } else {
        if (window.ethereum) {
          newProvider = new ethers.providers.Web3Provider(window.ethereum);
        } else {
          setError('Please install MetaMask or another Ethereum wallet extension');
          setIsConnecting(false);
          return;
        }
      }

      const accounts = await newProvider.send("eth_requestAccounts", []);
      
      if (accounts.length === 0) {
        throw new Error('No accounts found. Please unlock your wallet.');
      }

      // Get current network info but don't require specific network for connection
      const network = await newProvider.getNetwork();
      
      const newSigner = newProvider.getSigner();
      const newContract = new ethers.Contract(
        process.env.REACT_APP_CONTRACT_ADDRESS,
        contractAbi,
        newSigner
      );
      
      setProvider(newProvider);
      setSigner(newSigner);
      setContract(newContract);
      setAccount(accounts[0]);
      setIsConnected(true);
      setChainId(network.chainId);
      setNetworkName(getNetworkName(network.chainId));
      setError(null);
      
      localStorage.setItem('walletConnected', 'true');
      
      // Always fetch passport data from SKALE regardless of connected network
      fetchPassportData(accounts[0]);

    } catch (err) {
      console.error('Error connecting to wallet:', err);
      setError(err.message);
      localStorage.removeItem('walletConnected');
    } finally {
      setIsConnecting(false);
    }
  }, [isConnecting]);

  // Helper function to get network name
  const getNetworkName = (chainId) => {
    switch (chainId) {
      case 1: return 'Ethereum Mainnet';
      case 5: return 'Goerli Testnet';
      case 137: return 'Polygon';
      case 1020352220: return 'Neurith Testnet';
      default: return `Network ${chainId}`;
    }
  };

  // Network switching function for when SKALE transactions are needed
  const switchToSkaleNetwork = useCallback(async () => {
    if (!provider) {
      throw new Error('Wallet not connected');
    }

    try {
      const requiredChainId = 1020352220; // Neurith Testnet
      const currentNetwork = await provider.getNetwork();

      if (currentNetwork.chainId === requiredChainId) {
        return true; // Already on correct network
      }

      try {
        await provider.send('wallet_switchEthereumChain', [
          { chainId: '0x3cd156dc' }
        ]);
        return true;
      } catch (switchError) {
        if (switchError.code === 4902) {
          try {
            await provider.send('wallet_addEthereumChain', [
              {
                chainId: '0x3cd156dc',
                chainName: 'Neurith Testnet',
                nativeCurrency: {
                  name: 'SFUEL',
                  symbol: 'SFUEL',
                  decimals: 18
                },
                rpcUrls: [process.env.REACT_APP_SKALE_RPC],
                blockExplorerUrls: ['https://aware-fake-trim-testnet.explorer.testnet.skalenodes.com/']
              }
            ]);
            return true;
          } catch (addError) {
            throw new Error(`Failed to add Neurith Testnet: ${addError.message}`);
          }
        } else {
          throw new Error(`Failed to switch to Neurith Testnet: ${switchError.message}`);
        }
      }
    } catch (error) {
      console.error('Error switching network:', error);
      throw error;
    }
  }, [provider]);

  // Always read passport data from SKALE regardless of user's connected network
  const fetchPassportData = useCallback(async (address) => {
    if (!address) return;
    
    setPassportLoading(true);
    try {
      // Always use API service which should read from SKALE
      const { passport } = await ApiService.getPassportByAddress(address);
      setPassport(passport);
    } catch (error) {
      console.error('Error fetching passport:', error);
      setPassport(null);
    } finally {
      setPassportLoading(false);
    }
  }, []);

  const signMessage = useCallback(async (message) => {
    if (!signer) {
      throw new Error("Wallet not connected");
    }
    
    try {
      const timestamp = Date.now();
      const fullMessage = `${message}\nTimestamp: ${timestamp}\nWallet: ${account}`;
      
      const signature = await signer.signMessage(fullMessage);
      
      return {
        message: fullMessage,
        signature,
        address: account,
        timestamp
      };
    } catch (error) {
      console.error("Error signing message:", error);
      throw error;
    }
  }, [signer, account]);
  
  const createPassportWithSignature = useCallback(async (category, referralCode = '') => {
    if (!isConnected || !account) {
      return {
        success: false,
        error: 'Wallet not connected'
      };
    }
    
    if (!category) {
      return {
        success: false,
        error: 'Category is required'
      };
    }
    
    try {
      setPassportLoading(true);
      
      const signatureData = await signMessage(`I authorize the creation of a Social Passport of type ${category} for my wallet`);
      
      const queueResult = await ApiService.createPassportWithSignature(
        account, 
        signatureData.signature, 
        signatureData.message,
        signatureData.timestamp,
        category,
        referralCode
      );
      
      if (!queueResult.success) {
        if (queueResult.errorCategory === 'rate_limit') {
          return {
            success: false,
            error: queueResult.error || 'Passport creation limit reached',
            errorCategory: 'rate_limit',
            isPermanentFailure: true,
            suggestions: [
              'Only one passport can be created per network connection',
              'This prevents abuse of the referral system',
              'Try from a different network if this is a shared connection',
              'Contact support if you believe this is an error'
            ]
          };
        }
        
        return {
          success: false,
          error: queueResult.error,
          errorCategory: queueResult.errorCategory,
          isPermanentFailure: queueResult.isPermanentFailure,
          suggestions: queueResult.suggestions
        };
      }
      
      const finalResult = await pollTransactionStatus(queueResult.transactionId);
      
      if (finalResult.success) {
        await fetchPassportData(account);
        
        return {
          success: true,
          passportId: finalResult.result?.passportId
        };
      } else {
        return {
          success: false,
          error: finalResult.error,
          errorCategory: finalResult.errorCategory,
          isPermanentFailure: finalResult.isPermanentFailure,
          suggestions: finalResult.suggestions,
          technicalError: finalResult.technicalError
        };
      }
    } catch (error) {
      console.error('Error creating passport:', error);
      return {
        success: false,
        error: error.message
      };
    } finally {
      setPassportLoading(false);
    }
  }, [isConnected, account, fetchPassportData, signMessage]);
  
  const verifySocialAccountWithSignature = useCallback(async (platform) => {
    if (!isConnected || !account) {
      return {
        success: false,
        error: 'Wallet not connected'
      };
    }
    
    try {
      const signatureData = await signMessage(`I authorize the verification of my ${platform} account for my wallet`);
      
      let result;
      
      switch (platform) {
        case 'twitter':
          result = await ApiService.startTwitterVerificationWithSignature(
            account,
            signatureData.signature,
            signatureData.message,
            signatureData.timestamp
          );
          break;
          
        case 'github':
          result = await ApiService.startGitHubVerificationWithSignature(
            account,
            signatureData.signature,
            signatureData.message,
            signatureData.timestamp
          );
          break;
          
        default:
          result = await ApiService.startVerificationWithSignature(
            platform,
            account,
            signatureData.signature,
            signatureData.message,
            signatureData.timestamp
          );
      }
      
      return result;
    } catch (error) {
      console.error(`Error starting ${platform} verification:`, error);
      throw error;
    }
  }, [isConnected, account, signMessage]);
  
  const completeVerificationWithSignature = useCallback(async (platform) => {
    if (!isConnected || !account) {
      return {
        success: false,
        error: 'Wallet not connected'
      };
    }
    
    try {
      const signatureData = await signMessage(`I authorize the completion of my ${platform} account verification for my wallet`);
      
      let result;
      
      switch (platform) {
        case 'twitter':
          result = await ApiService.completeTwitterVerificationWithSignature(
            account,
            signatureData.signature,
            signatureData.message,
            signatureData.timestamp
          );
          break;
          
        case 'github':
          result = await ApiService.completeGitHubVerificationWithSignature(
            account,
            signatureData.signature,
            signatureData.message,
            signatureData.timestamp
          );
          break;
          
        default:
          result = await ApiService.completeVerificationWithSignature(
            platform,
            account,
            signatureData.signature,
            signatureData.message,
            signatureData.timestamp
          );
      }
      
      if (result && result.transactionId) {
        const finalResult = await pollTransactionStatus(result.transactionId);
        
        if (finalResult.success) {
          await fetchPassportData(account);
          return { success: true, alreadyVerified: false };
        } else {
          return {
            success: false,
            error: finalResult.error,
            errorCategory: finalResult.errorCategory,
            isPermanentFailure: finalResult.isPermanentFailure,
            suggestions: finalResult.suggestions
          };
        }
      } else if (result && (result.success || result.alreadyVerified)) {
        if (result.success || result.alreadyVerified) {
          await fetchPassportData(account);
        }
        return result;
      } else {
        throw new Error(result?.error || "Verification failed without specific error");
      }
    } catch (error) {
      console.error(`Error completing ${platform} verification:`, error);
      throw error; 
    }
  }, [isConnected, account, signMessage, fetchPassportData]);

  const createPassport = useCallback(async () => {
    if (!isConnected || !account) {
      return {
        success: false,
        error: 'Wallet not connected'
      };
    }
    
    try {
      setPassportLoading(true);
      
      const result = await ApiService.createPassport(account);
      
      await fetchPassportData(account);
      
      return {
        success: true,
        passportId: result.passportId
      };
    } catch (error) {
      console.error('Error creating passport:', error);
      return {
        success: false,
        error: error.message
      };
    } finally {
      setPassportLoading(false);
    }
  }, [isConnected, account, fetchPassportData]);

  const disconnectWallet = useCallback(() => {
    setProvider(null);
    setSigner(null);
    setContract(null);
    setAccount(null);
    setIsConnected(false);
    setError(null);
    setPassport(null);
    setSupportedCategories([]);
    
    // Also disconnect Solana wallet
    setSolanaWallet(null);
    setSolanaConnected(false);
    setSolanaPublicKey(null);
    setSolanaWalletType(null);
    
    localStorage.removeItem('walletConnected');
    
    if (!isMobile()) {
      window.location.reload();
    }
  }, []);

  // Solana wallet methods (keeping all existing functionality)
  const getAvailableSolanaWallets = useCallback(() => {
    const wallets = [];
    
    if (window.solana && window.solana.isPhantom) {
      wallets.push({
        name: 'Phantom',
        type: 'phantom',
        wallet: window.solana,
        icon: '👻'
      });
    }
    
    let solflareWallet = null;
    
    if (window.solflare && window.solflare.isSolflare) {
      solflareWallet = window.solflare;
    } else if (window.solana && !window.solana.isPhantom && window.solana.isSolflare) {
      solflareWallet = window.solana;
    } else if (window.solana && window.solana.isSolflare) {
      solflareWallet = window.solana;
    }
    
    if (solflareWallet) {
      wallets.push({
        name: 'Solflare',
        type: 'solflare', 
        wallet: solflareWallet,
        icon: '🔥'
      });
    }
    
    return wallets;
  }, []);

  const connectSolanaWallet = useCallback(async (preferredWallet = null) => {
    try {
      const availableWallets = getAvailableSolanaWallets();
      
      if (availableWallets.length === 0) {
        throw new Error('No Solana wallets found. Please install Phantom or Solflare wallet.');
      }
      
      let selectedWallet = null;
      
      if (preferredWallet) {
        selectedWallet = availableWallets.find(w => w.type === preferredWallet);
        if (!selectedWallet) {
          throw new Error(`${preferredWallet} wallet not found. Please install it or try a different wallet.`);
        }
      } else {
        selectedWallet = availableWallets.find(w => w.type === 'phantom') || availableWallets[0];
      }
      
      let response;
      
      if (selectedWallet.type === 'solflare') {
        response = await selectedWallet.wallet.connect();
        
        if (response && response.publicKey) {
          // Standard format
        } else if (response && typeof response === 'string') {
          response = { publicKey: { toString: () => response } };
        } else if (selectedWallet.wallet.publicKey) {
          response = { publicKey: selectedWallet.wallet.publicKey };
        } else {
          console.error('Unexpected Solflare response format:', response);
          throw new Error('Solflare connection returned unexpected format');
        }
      } else {
        response = await selectedWallet.wallet.connect();
      }
      
      let publicKeyString = null;
      
      if (response && response.publicKey) {
        if (typeof response.publicKey.toString === 'function') {
          publicKeyString = response.publicKey.toString();
        } else if (typeof response.publicKey === 'string') {
          publicKeyString = response.publicKey;
        }
      } else if (selectedWallet.wallet.publicKey) {
        if (typeof selectedWallet.wallet.publicKey.toString === 'function') {
          publicKeyString = selectedWallet.wallet.publicKey.toString();
        } else if (typeof selectedWallet.wallet.publicKey === 'string') {
          publicKeyString = selectedWallet.wallet.publicKey;
        }
      }
      
      if (publicKeyString) {
        setSolanaWallet(selectedWallet.wallet);
        setSolanaConnected(true);
        setSolanaPublicKey(publicKeyString);
        setSolanaWalletType(selectedWallet.type);
        
        return {
          success: true,
          publicKey: publicKeyString,
          walletType: selectedWallet.type,
          walletName: selectedWallet.name
        };
      } else {
        console.error('Could not extract public key from response:', response);
        throw new Error(`Failed to get public key from ${selectedWallet.name} wallet`);
      }
    } catch (error) {
      console.error('Error connecting Solana wallet:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }, [getAvailableSolanaWallets]);

  const connectSpecificSolanaWallet = useCallback(async (walletType) => {
    return await connectSolanaWallet(walletType);
  }, [connectSolanaWallet]);

  const disconnectSolanaWallet = useCallback(async () => {
    try {
      if (solanaWallet && solanaWallet.disconnect) {
        await solanaWallet.disconnect();
      }
      
      setSolanaWallet(null);
      setSolanaConnected(false);
      setSolanaPublicKey(null);
      setSolanaWalletType(null);
      
      console.log('Solana wallet disconnected');
    } catch (error) {
      console.error('Error disconnecting Solana wallet:', error);
    }
  }, [solanaWallet]);

  const signSolanaMessage = useCallback(async (message) => {
    if (!solanaWallet || !solanaConnected) {
      throw new Error('Solana wallet not connected');
    }
    
    try {
      const encodedMessage = new TextEncoder().encode(message);
      
      let signedMessage;
      
      if (solanaWalletType === 'phantom') {
        signedMessage = await solanaWallet.signMessage(encodedMessage, 'utf8');
      } else if (solanaWalletType === 'solflare') {
        try {
          signedMessage = await solanaWallet.signMessage(encodedMessage);
          console.log('Solflare signing method 1 successful');
        } catch (error1) {
          console.log('Solflare method 1 failed, trying method 2:', error1.message);
          try {
            signedMessage = await solanaWallet.signMessage(encodedMessage, 'utf8');
            console.log('Solflare signing method 2 successful');
          } catch (error2) {
            console.log('Solflare method 2 failed, trying method 3:', error2.message);
            signedMessage = await solanaWallet.signMessage(message);
            console.log('Solflare signing method 3 successful');
          }
        }
      } else {
        signedMessage = await solanaWallet.signMessage(encodedMessage);
      }
      
      let signature, publicKey;
      
      if (signedMessage && signedMessage.signature) {
        signature = signedMessage.signature;
        publicKey = signedMessage.publicKey;
      } else if (Array.isArray(signedMessage)) {
        signature = signedMessage[0];
        publicKey = signedMessage[1] || solanaPublicKey;
      } else {
        signature = signedMessage;
        publicKey = solanaPublicKey;
      }
      
      if (publicKey && typeof publicKey.toString === 'function') {
        publicKey = publicKey.toString();
      } else if (!publicKey || typeof publicKey !== 'string') {
        publicKey = solanaPublicKey;
      }
      
      // Send signature as-is - let the server handle the format conversion
      return {
        signature: signature,
        publicKey: publicKey
      };
    } catch (error) {
      console.error(`Error signing message with ${solanaWalletType} wallet:`, error);
      throw error;
    }
  }, [solanaWallet, solanaConnected, solanaWalletType, solanaPublicKey]);

  // Complete Solana verification flow
  const verifySolanaWithSignature = useCallback(async () => {
    // Check prerequisites
    if (!isConnected || !account) {
      console.error('Ethereum wallet not connected');
      return {
        success: false,
        error: 'Ethereum wallet not connected'
      };
    }
    
    try {
      const timestamp = Date.now();
      const message = `I authorize linking my Solana address to my Passly passport\nSolana: ${solanaPublicKey}\nWallet: ${account}\nTimestamp: ${timestamp}`;
      
      // Sign with Solana wallet
      const { signature } = await signSolanaMessage(message);
      
      // Send to backend for verification using the correct API endpoint
      const result = await ApiService.verifySolanaWithSignature(
        account,
        solanaPublicKey,
        message,
        signature,
        timestamp
      );
      
      if (result.success) {
        // Poll for completion if we get a transaction ID
        if (result.transactionId) {
          console.log('Solana verification queued, polling for completion...');
          const finalResult = await pollTransactionStatus(result.transactionId);
          
          if (finalResult.success) {
            await fetchPassportData(account);
            return { success: true };
          } else {
            return {
              success: false,
              error: finalResult.error,
              errorCategory: finalResult.errorCategory,
              isPermanentFailure: finalResult.isPermanentFailure,
              suggestions: finalResult.suggestions
            };
          }
        } else {
          // Direct success without queue
          await fetchPassportData(account);
          return { success: true };
        }
      } else {
        return {
          success: false,
          error: result.error,
          errorCategory: result.errorCategory
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to verify Solana address',
        errorCategory: 'verification_failed'
      };
    }
  }, [isConnected, account, solanaConnected, solanaPublicKey, signSolanaMessage, fetchPassportData]);

  // Effects
  useEffect(() => {
    if (window.ethereum) {
      const handleAccountsChanged = (accounts) => {
        if (accounts.length === 0) {
          disconnectWallet();
        } else if (accounts[0] !== account) {
          setAccount(accounts[0]);
          fetchPassportData(accounts[0]);
        }
      };

      const handleChainChanged = (chainId) => {
        // Update chain info but don't force disconnection
        const newChainId = parseInt(chainId, 16);
        setChainId(newChainId);
        setNetworkName(getNetworkName(newChainId));
        
        // Only reload on desktop for major network changes
        if (!isMobile()) {
          window.location.reload();
        }
      };

      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('chainChanged', handleChainChanged);

      // Auto-connect if previously connected
      if (localStorage.getItem('walletConnected') === 'true' && !isConnected && !isConnecting) {
        connectWallet();
      }

      return () => {
        if (window.ethereum.removeListener) {
          window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
          window.ethereum.removeListener('chainChanged', handleChainChanged);
        }
      };
    }
  }, [account, connectWallet, disconnectWallet, fetchPassportData, isConnected, isConnecting]);

  useEffect(() => {
    if (isConnected) {
      localStorage.setItem('walletConnected', 'true');
    } else {
      localStorage.removeItem('walletConnected');
    }
  }, [isConnected]);

  const value = {
    // Ethereum wallet state
    provider,
    signer,
    contract,
    account,
    isConnected,
    error,
    chainId,
    networkName,
    passport,
    passportLoading,
    supportedCategories,
    categoriesLoading,
    isConnecting,
    
    // Ethereum wallet methods
    connectWallet,
    disconnectWallet,
    fetchPassportData,
    createPassport,
    signMessage,
    createPassportWithSignature,
    verifySocialAccountWithSignature,
    completeVerificationWithSignature,
    switchToSkaleNetwork, // For when SKALE transactions are needed
    
    // Solana wallet state
    solanaWallet,
    solanaConnected,
    solanaPublicKey,
    solanaWalletType,
    
    // Solana wallet methods
    getAvailableSolanaWallets,
    connectSolanaWallet,
    connectSpecificSolanaWallet,
    disconnectSolanaWallet,
    signSolanaMessage,
    verifySolanaWithSignature,
  };

  return (
    <Web3Context.Provider value={value}>
      {children}
    </Web3Context.Provider>
  );
}